// src/pages/ProfileLayout.jsx
import { useState, createContext, useContext, useCallback, useEffect } from "react";
import { Outlet, NavLink } from "react-router-dom";
import {
  User,
  CreditCard,
  Receipt,
  Mail,
  Settings,
  Edit,
  MessageSquare,
  ChevronRight,
  Lock,
  Cloud
} from "lucide-react";
import EditProfileModal from "../components/EditProfileModal";
import { LoginContext } from "../LoginContext";
import { MessagesContext } from "../MessagesContext";
import { useTranslation } from "react-i18next";
import { getBaseUrl } from "../config/apiConfig";
import apiService from "../backend/api";

// Context für Profile-Updates
export const ProfileUpdateContext = createContext();

export default function ProfileLayout() {
    const [showEditModal, setShowEditModal] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const { loginInfo } = useContext(LoginContext);
    const { unreadCount, updateUnreadCount, newCount, updateNewCount } = useContext(MessagesContext);
    const loggedInUser = loginInfo?.username;
    const { t } = useTranslation();
    // Lade counts beim Komponenten-Mount und alle 30 Sekunden
    useEffect(() => {
        if (loggedInUser) {
            updateUnreadCount();
            updateNewCount();

            const interval = setInterval(() => {
                updateUnreadCount();
                updateNewCount();
            }, 30000);

            return () => clearInterval(interval);
        }
    }, [loggedInUser, updateUnreadCount, updateNewCount]);

    const loadProfile = useCallback(async () => {
        if (!loggedInUser) return;
        try {
            const data = await apiService.getProfile(loggedInUser);
            if (data?.error) {
                console.error("API error loading profile:", data.error);
                setProfileData(null);
            } else {
                setProfileData(data);
            }
        } catch (error) {
            console.error("Fehler beim Laden des Profils:", error);
            setProfileData(null);
        }
    }, [loggedInUser]);

    const updateProfile = useCallback((updatedData) => {
        setProfileData(updatedData);
    }, []);

    const navItems = [
        { to: "details", icon: <User size={18}/>, label: t("profile.Profil Details", "Profil Details") },
        { to: "subscription", icon: <CreditCard size={18}/>, label: t("profile.Abonnementdetails", "Abonnementdetails") },
        { to: "billing", icon: <Receipt size={18}/>, label: t("profile.Rechnungen", "Rechnungen") },
        { to: "healthcloud", icon: <Cloud size={18}/>, label: t("profile.Healthcloud", "Healthcloud") },
        { to: "change-password", icon: <Lock size={18}/>, label: t("profile.passwort_andern", "Passwort ändern") },
        {
            to: "messages",
            icon: <MessageSquare size={18}/>,
            label: t("profile.Nachrichten", "Nachrichten"),
            badge: newCount
        },
        { to: "accessibility", icon: <Settings size={18}/>, label: t("profile.Barrierefreiheit", "Barrierefreiheit") },
    ];

    return (
        <div className="flex-1 flex flex-col md:flex-row bg-slate-50 dark:bg-slate-900 font-sans relative transition-colors duration-300">
            {/* Sidebar Container */}
            <aside className="w-80 bg-[#0f7699] dark:bg-slate-800 text-white hidden md:flex flex-col shadow-2xl z-20">
                {/* Sticky Wrapper for Sidebar Content */}
                <div className="sticky top-20 h-[calc(100vh-80px)] flex flex-col">
                    <div className="flex-1 overflow-y-auto py-8 text-white/90">
                        <nav className="space-y-1 px-4">
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({ isActive }) => `
                                        group flex items-center justify-between px-6 py-4 rounded-2xl transition-all duration-300
                                        ${isActive
                                            ? "bg-white dark:bg-slate-700 text-[#0f7699] dark:text-[#4fc3f7] shadow-lg shadow-black/10 translate-x-1"
                                            : "hover:bg-white/10 text-white/80 hover:text-white"
                                        }
                                    `}
                                >
                                    {({ isActive }) => (
                                        <>
                                            <div className="flex items-center gap-4">
                                                <div className={`transition-colors duration-300 ${isActive ? "text-[#0f7699] dark:text-[#4fc3f7]" : "text-white/60 group-hover:text-white"}`}>
                                                    {item.icon}
                                                </div>
                                                <span className="text-[13px] font-bold tracking-wide">{item.label}</span>
                                            </div>

                                            {item.badge > 0 ? (
                                                <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full border-2 border-white shadow-sm animate-pulse">
                                                    {item.badge > 99 ? "99+" : item.badge}
                                                </span>
                                            ) : (
                                                <ChevronRight size={14} className="opacity-0 group-hover:opacity-40 transition-opacity" />
                                            )}
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </nav>
                    </div>

                    {/* Sidebar Footer */}
                    <div className="p-6 border-t border-white/10">
                        <button
                            onClick={() => setShowEditModal(true)}
                            className="w-full flex items-center gap-4 px-6 py-4 rounded-2xl bg-white/10 hover:bg-white dark:hover:bg-slate-700 text-white hover:text-[#0f7699] dark:hover:text-[#4fc3f7] transition-all duration-300 group"
                        >
                            <Edit size={18} className="text-white/60 group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7] transition-colors" />
                            <span className="text-[13px] font-bold tracking-wide">{t("profile.Profil bearbeiten", "Profil bearbeiten")}</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile Profile Navigation */}
            <div className="md:hidden sticky top-20 z-40 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 overflow-x-auto scrollbar-hide">
                <nav className="flex px-3 py-2 gap-1 min-w-max">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) => `
                                flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all shrink-0
                                ${isActive
                                    ? "bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900"
                                    : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                                }
                            `}
                        >
                            {item.icon}
                            <span className="hidden sm:inline">{item.label}</span>
                            {item.badge > 0 && (
                                <span className="bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none">
                                    {item.badge > 99 ? "99+" : item.badge}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>
            </div>

            {/* Main Content Area */}
            <main className="flex-1 p-4 md:p-8 lg:p-12 overflow-x-hidden">
                <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-right-4 duration-500">
                    <ProfileUpdateContext.Provider value={{
                        profileData,
                        loadProfile,
                        updateProfile
                    }}>
                        <Outlet />
                    </ProfileUpdateContext.Provider>
                </div>
            </main>

            {/* Edit Profile Modal */}
            {showEditModal && (
                <EditProfileModal
                    onClose={() => setShowEditModal(false)}
                    onSave={(updatedProfile) => {
                        setShowEditModal(false);
                        updateProfile(updatedProfile);
                    }}
                />
            )}
        </div>
    );
}

